'use client';

import { ChangeEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import MaskedInput from 'react-text-mask';
import { ReactComponent as Calendar } from '@assets/icons/calendar.svg';
import { dateMask } from 'constant';
import dayjs from 'dayjs';
import { FormikErrors } from 'formik';

import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useTheme
} from '@chakra-ui/react';

import { getMaxValidDate, getParsedDate } from 'utils';

const authFieldStyles = {
  wrapper: {
    height: '62px'
  },
  insideBox: {
    borderLeftRadius: '5px'
  },
  valueWrapper: {
    fontWeight: 600,
    py: 0,
    fontSize: { base: 16, sm: 18, md: 19, xl: 22 }
  },
  placeholder: {
    fontSize: '15px',
    fontFamily: 'main',
    textTransform: 'uppercase',
    letterSpacing: '0.5',
    color: 'gray.600',
    fontWeight: 400,
    position: 'absolute',
    top: '18px'
  },
  value: {
    fontSize: [16, 18, 22],
    fontFamily: 'main',
    color: 'gray.800',
    fontWeight: 'bold'
  }
};

const profileFieldStyles = {
  wrapper: {
    height: '57px'
  },
  insideBox: {
    height: '38px'
  },
  valueWrapper: {
    paddingLeft: '10px',
    paddingRight: '25px'
  },
  placeholder: {
    fontSize: '15px',
    fontFamily: 'main',
    textTransform: 'uppercase',
    letterSpacing: '0.5',
    color: 'gray.600'
  },
  value: {
    fontSize: '15px',
    lineHeight: '1px',
    color: 'gray.800'
  }
};

const defaultDate = dayjs(new Date()).add(-20, 'years').format('01/01/YYYY');

interface DatePickerProps extends Omit<ReactDatePickerProps, 'onChange'> {
  value: string;
  iconBg: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onTouched: () => void;
  placeholder?: string;
  leftIcon?: ReactNode;
  isAuthField?: boolean;
  touched: boolean;
  error?:
    | string
    | string[]
    | FormikErrors<Record<string, unknown>>
    | FormikErrors<Record<string, unknown>>[];
  label?: string;
  isRequired?: boolean;
  autoFocus?: boolean;
}

const DatePicker = ({
  value,
  onChange,
  onBlur,
  leftIcon,
  iconBg,
  placeholder,
  isAuthField,
  label,
  error,
  onTouched,
  touched,
  isRequired,
  autoFocus = false
}: DatePickerProps) => {
  const { colors } = useTheme();
  const dataPickerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarValue, setIsCalendarValue] = useState(true);

  const todayDate = new Date();
  const inputStyle = isAuthField ? authFieldStyles : profileFieldStyles;
  const isErrorExist = error && typeof error === 'string' && touched;

  const toggleIsOpen = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleDayPickerChange = (date: Date) => {
    const formattedDate = dayjs(date).format('MM/DD/YYYY');

    if (!isCalendarValue) {
      setIsCalendarValue(true);
    }

    onTouched();
    onChange(formattedDate);
    setIsOpen(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isCalendarValue) {
      setIsCalendarValue(false);
    }

    const inputDate = event.target.value;
    const maxValidDate = getMaxValidDate(inputDate);
    onChange(maxValidDate);
  };

  const handleClickOutside = (event: MouseEvent<HTMLElement>) => {
    const refElement = dataPickerRef?.current;
    const isRefContainsInitialTrigger = refElement?.contains(
      event.target as Node
    );

    if (refElement && !isRefContainsInitialTrigger) {
      setIsOpen(false);
    }
  };

  const handleInputFocus = () => {
    onTouched();
    setIsOpen(false);

    if (isCalendarValue) {
      setIsCalendarValue(false);
      onChange('');
    }
  };

  return (
    <FormControl
      width='100%'
      sx={inputStyle.wrapper}
      isInvalid={!!isErrorExist}
      isRequired={isRequired}
      className='date-picker-field'
    >
      {label && (
        <FormLabel
          fontSize='11px'
          color='gray.375'
          textTransform='uppercase'
          mb='5px'
          mr={0}
          minHeight='14px'
          requiredIndicator={
            <Text as='sup' color='red.300' marginLeft='2px'>
              *
            </Text>
          }
        >
          {label}
        </FormLabel>
      )}

      <Flex
        ref={dataPickerRef}
        position='relative'
        height='100%'
        width='100%'
        cursor='pointer'
        border={isAuthField ? '1px solid' : 'none'}
        borderColor={isErrorExist ? 'red.500' : 'transparent'}
        sx={inputStyle.insideBox}
      >
        {leftIcon && (
          <Flex
            alignItems='center'
            justifyContent='center'
            flexShrink={0}
            height='100% !important'
            width='60px'
            bg={iconBg}
            style={{ height: 60, width: 60 }}
            borderLeftRadius='5px'
            cursor='auto'
          >
            {leftIcon}
          </Flex>
        )}

        <Input
          as={MaskedInput}
          type='text'
          variant={isAuthField ? 'auth' : 'brand'}
          mask={dateMask}
          guide={!isCalendarValue}
          keepCharPositions
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={onBlur}
          alignItems='center'
          pr='40px'
          pl='20px'
          height='100%'
          width='100%'
          margin={0}
          autoFocus={autoFocus}
          _placeholder={inputStyle.placeholder}
          sx={inputStyle.valueWrapper}
        />

        <Flex
          position='absolute'
          height='100%'
          right={
            isAuthField ? { base: '23px', lg: '13px', xl: '23px' } : '15px'
          }
          alignItems='center'
          onClick={toggleIsOpen}
          zIndex={1}
          _hover={{
            '& svg': {
              fill: 'gray.400'
            }
          }}
        >
          <Calendar width={24} height={24} fill={colors.gray[250]} />
        </Flex>
      </Flex>

      <Box
        as={ReactDatePicker}
        position='absolute'
        top={0}
        right={0}
        bottom={0}
        left={0}
        selected={value ? getParsedDate(value) : getParsedDate(defaultDate)}
        onClickOutside={handleClickOutside}
        showPopperArrow={false}
        open={isOpen}
        dateFormat='MM-dd-yyyy'
        showMonthDropdown
        showYearDropdown
        yearDropdownItemNumber={99}
        dropdownMode='scroll'
        maxDate={todayDate}
        scrollableYearDropdown
        onChange={handleDayPickerChange}
        onBlur={onBlur}
        showMonthYearPicker={false}
        popperModifiers={[
          {
            name: 'offset',
            options: {
              offset: [30, -20]
            }
          },
          {
            name: 'preventOverflow',
            options: {
              rootBoundary: 'viewport',
              tether: false,
              altAxis: true
            }
          }
        ]}
        fixedHeight
      />
    </FormControl>
  );
};

export default DatePicker;
